import * as React from 'react';
import {useCallback, useState} from 'react';
import {Button, Divider, IconButton, Tooltip} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {Alert, Input, Snackbar} from "@mui/material";
import {makeApiCall} from "../../hooks/CancellableApiCall";
import {Edit} from "@mui/icons-material";
import {ProfileResponse} from "../../data/ProfileResponse";
import useUser, {UserContextProps} from "../../hooks/useUser";

interface EditUserFormProps {
    user: ProfileResponse
    userApiResponse: UserContextProps
}

type alertType = "success" | "error" | "info"

export default function EditUserForm(props: EditUserFormProps) {
    const [open, setOpen] = useState(false);
    const [nick, setNick] = useState(props.user.nick)
    const [aboutMe, setAboutMe] = useState(props.user.aboutMe ?? "")
    const [baseProfileUrl, setBaseProfileUrl] = useState(props.user.avatarLink)
    const [medProfileUrl, setMedProfileUrl] = useState(props.user.mediumAvatarLink)
    const [fullProfileUrl, setFullProfileUrl] = useState(props.user.fullAvatarLink)
    const [badgeNumber, setBadeNumber] = useState(props.user.badgeNumber)
    const [showAlert, setShowAlert] = useState(false)
    const [alertType, setAlertType] = useState<alertType>("success")
    const [alertText, setAlertText] = useState("")
    const user = useUser()

    const setAlert = useCallback((type: alertType, text: string) => {
        setAlertType(type)
        setAlertText(text)
        setShowAlert(true)
    }, [setAlertType, setAlertText, setShowAlert])

    const handleClose = () => {
        setOpen(false);
    };

    return (
        <React.Fragment>
            <Tooltip title="Edit Account">
            <IconButton onClick={() => setOpen(true)} size="small" color="warning"><Edit/></IconButton>
            </Tooltip>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Editing {props.user.nick}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Username
                    </DialogContentText>
                    <Input id={"nick"} value={nick} size="small" sx={{width: 500}}
                           onChange={(event) => setNick(event.target.value)}
                    ></Input>
                   <DialogContentText>
                        About Me
                    </DialogContentText>
                    <Input fullWidth multiline rows={4} placeholder="Tell us about yourself..." value={aboutMe}
                           onChange={(event) => setAboutMe(event.target.value)}
                    ></Input>
                </DialogContent>
                <DialogContent>
                    <DialogContentText>
                        Base Avatar URL
                    </DialogContentText>
                    <Input id={"p1"} value={baseProfileUrl} size="small" sx={{width: 500}}
                           onChange={(event) => setBaseProfileUrl(event.target.value)}
                    ></Input>
                    <DialogContentText>
                        Medium Avatar URL
                    </DialogContentText>
                    <Input id={"p2"} value={medProfileUrl} size="small" sx={{width: 500}}
                           onChange={(event) => setMedProfileUrl(event.target.value)}
                    ></Input>
                    <DialogContentText>
                        Full Avatar URL
                    </DialogContentText>
                    <Input id={"p3"} value={fullProfileUrl} size="small" sx={{width: 500}}
                           onChange={(event) => setFullProfileUrl(event.target.value)}
                    ></Input>
                </DialogContent>
                {user.hasPermissions("developer:badgeNumberPanel") &&
                <DialogContent>
                    <DialogContentText>
                        Donation Controls
                    </DialogContentText>
                    <Input id={"p3"} value={badgeNumber} size="small" sx={{width: 500}}
                           onChange={(event) => setBadeNumber(Number(event.target.value))}
                    ></Input>
                    <Button color={"error"} onClick={() => {
                        makeApiCall({
                            url: `/api/user/admin/reset-badge/${props.user.communityId}`,
                            method: "DELETE",
                            onLoadedCallback: () => {
                                props.userApiResponse.refresh()
                                handleClose()
                            },
                            onError: () => {
                                setAlert("error", "Failed to revoke badge number")
                            }
                    })}}>Reset Badge</Button>
                    <Button color={"success"} onClick={() => {
                        makeApiCall({
                            url: `/api/user/admin/set-badge/${props.user.communityId}?badge=${badgeNumber}`,
                            method: "PATCH",
                            onLoadedCallback: () => {
                                props.userApiResponse.refresh()
                                handleClose()
                            },
                            onError: () => {
                                setAlert("success", "Granted a donated badge number")
                            }
                        })}}>Grant Custom Badge</Button>
                    <Divider />
                </DialogContent> }
                <DialogActions>
                    <Button color={"info"} onClick={handleClose}>Close</Button>
                    <Button color={"success"} onClick={() =>
                        makeApiCall({
                            url: `/api/user/${props.user.communityId}`,
                            method: "PATCH",
                            body: {
                                nick: nick,
                                aboutMe: aboutMe,
                                baseAvatarLink: baseProfileUrl,
                                mediumAvatarLink: medProfileUrl,
                                fullAvatarLink: fullProfileUrl
                            },
                            onLoadedCallback: () => {
                                props.userApiResponse.refresh()
                                handleClose()
                            },
                            onError: () => {
                                setAlert("error", "Profile failed to edit, check badge and nick for conflicts")
                            }
                        })
                    }>Edit</Button>
                </DialogActions>
            </Dialog>
            <Snackbar open={showAlert} autoHideDuration={5000} onClose={() => setShowAlert(false)}>
                <Alert severity={alertType} onClose={() => setShowAlert(false)} sx={{width: "100%"}} variant="filled">
                    {alertText}
                </Alert>
            </Snackbar>
        </React.Fragment>
    );
}